import React, { useState, useMemo } from 'react';
import {
  Lock,
  MessageSquare,
  User,
  Calendar,
  Filter,
  RefreshCw,
  AlertCircle,
  Star,
  Wrench,
  Zap,
  Activity,
  Database,
  Upload,
  Trash2,
  File,
  Table,
  Download,
  Pencil,
  X,
  Check,
  Users,
} from 'lucide-react';

import {
  useListFeedback,
  useVerifyDevPassword,
  useListErrors,
  useUsageStats,
  useUsersOverview,
  useListUploads,
  useClearLegacyUploads,
  useDeleteUpload,
  useListDatabases,
  useSelectDatabase,
  useDatabaseStats,
  useTableSchema,
  useTableData,
  useExecuteQuery,
  useExportDatabase,
  useUpdateRows,
  useDeleteRows,
  useClearTable,
  useOrganizationsStats,
} from '../../api/hooks/dev.hook';
import type { FeedbackType } from '../../api/services/feedback';
import type {
  FeedbackEntry,
  ErrorLogEntry,
  UserUsageStats,
  UserOverviewEntry,
} from '../../api/services/dev';
import { cn } from '../../utils/cn';

const FEEDBACK_TYPE_CONFIG: Record<
  FeedbackType,
  { label: string; color: string; icon: React.ReactNode }
> = {
  general: {
    label: 'General',
    color: 'bg-slate-100 text-slate-700',
    icon: <MessageSquare className="w-3 h-3" />,
  },
  thought: {
    label: 'Thought',
    color: 'bg-violet-100 text-violet-700',
    icon: <Star className="w-3 h-3" />,
  },
  feature_wish: {
    label: 'Feature',
    color: 'bg-amber-100 text-amber-700',
    icon: <Zap className="w-3 h-3" />,
  },
  problem: {
    label: 'Problem',
    color: 'bg-rose-100 text-rose-700',
    icon: <Wrench className="w-3 h-3" />,
  },
};

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('en-US').format(num);
};

interface PasswordGateProps {
  onAuthenticated: (password: string) => void;
}

const PasswordGate: React.FC<PasswordGateProps> = ({ onAuthenticated }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const verifyPassword = useVerifyDevPassword();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await verifyPassword.mutateAsync(password);
      onAuthenticated(password);
    } catch {
      setError('Invalid password. Access denied.');
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 p-4">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzI3MjcyYSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30" />

      <div className="relative w-full max-w-md">
        <div className="bg-zinc-800/80 backdrop-blur-xl rounded-2xl border border-zinc-700/50 shadow-2xl overflow-hidden">
          <div className="p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 mb-4 shadow-lg shadow-emerald-500/20">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Developer Access</h1>
              <p className="mt-2 text-sm text-zinc-400">Enter password to access dashboard</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-zinc-300 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError(null);
                  }}
                  className={cn(
                    'w-full px-4 py-3 rounded-xl bg-zinc-900/50 border text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all duration-200',
                    error ? 'border-rose-500/50' : 'border-zinc-700 hover:border-zinc-600',
                  )}
                  placeholder="••••••••••••"
                  autoComplete="off"
                  autoFocus
                />
                {error && (
                  <p className="mt-2 text-sm text-rose-400 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1.5" />
                    {error}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={verifyPassword.isPending || !password}
                className={cn(
                  'w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200',
                  verifyPassword.isPending || !password
                    ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400 shadow-lg shadow-emerald-500/25',
                )}
              >
                {verifyPassword.isPending ? 'Verifying...' : 'Access Dashboard'}
              </button>
            </form>
          </div>
        </div>

        <p className="text-center mt-6 text-xs text-zinc-500">
          This page is for authorized personnel only.
        </p>
      </div>
    </div>
  );
};

type TabType = 'feedback' | 'errors' | 'users' | 'usage' | 'uploads' | 'organizations' | 'database';

interface DevDashboardProps {
  password: string;
}

const DevDashboard: React.FC<DevDashboardProps> = ({ password }) => {
  const [activeTab, setActiveTab] = useState<TabType>('feedback');

  return (
    <div
      className="min-h-screen bg-zinc-50"
      style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <header className="bg-white border-b border-zinc-200 shrink-0 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600">
                  <Database className="w-4 h-4 text-white" />
                </span>
                Developer Dashboard
              </h1>
              <p className="text-sm text-zinc-500 mt-0.5">Monitor feedback, errors, and usage</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-zinc-200 overflow-x-auto">
            {[
              { id: 'feedback' as TabType, label: 'Feedback', icon: MessageSquare },
              { id: 'errors' as TabType, label: 'Errors', icon: AlertCircle },
              { id: 'users' as TabType, label: 'Users', icon: User },
              { id: 'usage' as TabType, label: 'Usage', icon: Activity },
              { id: 'uploads' as TabType, label: 'Uploads', icon: Upload },
              { id: 'organizations' as TabType, label: 'Organizations', icon: Users },
              { id: 'database' as TabType, label: 'Database', icon: Database },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-zinc-500 hover:text-zinc-700',
                )}
              >
                <div className="flex items-center gap-2">
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </div>
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
        <div className="max-w-7xl mx-auto px-6 py-8 pb-20">
          {activeTab === 'feedback' && <FeedbackTab password={password} />}
          {activeTab === 'errors' && <ErrorsTab password={password} />}
          {activeTab === 'users' && <UsersTab password={password} />}
          {activeTab === 'usage' && <UsageTab password={password} />}
          {activeTab === 'uploads' && <UploadsTab password={password} />}
          {activeTab === 'organizations' && <OrganizationsTab password={password} />}
          {activeTab === 'database' && <DatabaseTab password={password} />}
        </div>
      </main>
    </div>
  );
};

// Feedback Tab Component
const FeedbackTab: React.FC<{ password: string }> = ({ password }) => {
  const [typeFilter, setTypeFilter] = useState<FeedbackType | ''>('');
  const [emailFilter, setEmailFilter] = useState('');

  const params = useMemo(
    () => ({
      password,
      feedback_type: typeFilter || undefined,
      email: emailFilter || undefined,
      limit: 200,
    }),
    [password, typeFilter, emailFilter],
  );

  const { data, isLoading, isError, refetch, isFetching } = useListFeedback(params);
  const entries = data?.entries ?? [];

  const uniqueEmails = useMemo(() => {
    const emails = new Set<string>();
    entries.forEach((entry) => emails.add(entry.email));
    return Array.from(emails).sort();
  }, [entries]);

  const stats = useMemo(() => {
    const byType: Record<FeedbackType, number> = {
      general: 0,
      thought: 0,
      feature_wish: 0,
      problem: 0,
    };
    entries.forEach((entry) => {
      byType[entry.feedback_type]++;
    });
    return byType;
  }, [entries]);

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {(
          Object.entries(FEEDBACK_TYPE_CONFIG) as [
            FeedbackType,
            (typeof FEEDBACK_TYPE_CONFIG)[FeedbackType],
          ][]
        ).map(([type, config]) => (
          <button
            key={type}
            onClick={() => setTypeFilter(typeFilter === type ? '' : type)}
            className={cn(
              'p-4 rounded-xl border transition-all duration-200 text-left',
              typeFilter === type
                ? 'bg-zinc-900 border-zinc-900 text-white'
                : 'bg-white border-zinc-200 hover:border-zinc-300 hover:shadow-sm',
            )}
          >
            <div className="flex items-center gap-2 mb-2">
              <span
                className={cn(
                  'p-1.5 rounded-lg',
                  typeFilter === type ? 'bg-white/10' : config.color,
                )}
              >
                {config.icon}
              </span>
              <span className="text-sm font-medium">{config.label}</span>
            </div>
            <p
              className={cn(
                'text-2xl font-bold',
                typeFilter === type ? 'text-white' : 'text-zinc-900',
              )}
            >
              {stats[type]}
            </p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-white rounded-xl border border-zinc-200">
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Filter className="w-4 h-4" />
          <span className="font-medium">Filters:</span>
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as FeedbackType | '')}
          className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
        >
          <option value="">All types</option>
          {Object.entries(FEEDBACK_TYPE_CONFIG).map(([type, config]) => (
            <option key={type} value={type}>
              {config.label}
            </option>
          ))}
        </select>
        <select
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
        >
          <option value="">All users</option>
          {uniqueEmails.map((email) => (
            <option key={email} value={email}>
              {email}
            </option>
          ))}
        </select>
        {(typeFilter || emailFilter) && (
          <button
            onClick={() => {
              setTypeFilter('');
              setEmailFilter('');
            }}
            className="text-sm text-zinc-500 hover:text-zinc-700 underline underline-offset-2"
          >
            Clear filters
          </button>
        )}
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className={cn(
            'ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            isFetching
              ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
              : 'bg-zinc-900 text-white hover:bg-zinc-800',
          )}
        >
          <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-zinc-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading feedback...</span>
          </div>
        </div>
      ) : isError ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
            <p className="text-zinc-600 font-medium">Failed to load feedback</p>
            <button
              onClick={() => refetch()}
              className="mt-4 text-sm text-emerald-600 hover:text-emerald-700 underline underline-offset-2"
            >
              Try again
            </button>
          </div>
        </div>
      ) : entries.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <MessageSquare className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">No feedback found</p>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="bg-white rounded-xl border border-zinc-200 p-5 hover:shadow-md hover:border-zinc-300 transition-all"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                    FEEDBACK_TYPE_CONFIG[entry.feedback_type].color,
                  )}
                >
                  {FEEDBACK_TYPE_CONFIG[entry.feedback_type].icon}
                  {FEEDBACK_TYPE_CONFIG[entry.feedback_type].label}
                </span>
                <time className="text-xs text-zinc-400 whitespace-nowrap flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDateTime(entry.created_at)}
                </time>
              </div>
              <p className="text-zinc-800 text-sm leading-relaxed whitespace-pre-wrap mb-4">
                {entry.feedback_text}
              </p>
              <div className="flex items-center gap-2 text-xs text-zinc-500 pt-3 border-t border-zinc-100">
                <User className="w-3.5 h-3.5" />
                <span className="font-medium">{entry.email}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Errors Tab Component
const ErrorsTab: React.FC<{ password: string }> = ({ password }) => {
  const [errorTypeFilter, setErrorTypeFilter] = useState('');
  const [userEmailFilter, setUserEmailFilter] = useState('');

  const params = useMemo(
    () => ({
      password,
      error_type: errorTypeFilter || undefined,
      user_email: userEmailFilter || undefined,
      limit: 100,
    }),
    [password, errorTypeFilter, userEmailFilter],
  );

  const { data, isLoading, isError, refetch, isFetching } = useListErrors(params);
  const entries = data?.entries ?? [];

  const uniqueErrorTypes = useMemo(() => {
    const types = new Set<string>();
    entries.forEach((entry) => types.add(entry.error_type));
    return Array.from(types).sort();
  }, [entries]);

  const uniqueEmails = useMemo(() => {
    const emails = new Set<string>();
    entries.forEach((entry) => {
      if (entry.user_email) emails.add(entry.user_email);
    });
    return Array.from(emails).sort();
  }, [entries]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-white rounded-xl border border-zinc-200">
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Filter className="w-4 h-4" />
          <span className="font-medium">Filters:</span>
        </div>
        <select
          value={errorTypeFilter}
          onChange={(e) => setErrorTypeFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
        >
          <option value="">All error types</option>
          {uniqueErrorTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        <select
          value={userEmailFilter}
          onChange={(e) => setUserEmailFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
        >
          <option value="">All users</option>
          {uniqueEmails.map((email) => (
            <option key={email} value={email}>
              {email}
            </option>
          ))}
        </select>
        {(errorTypeFilter || userEmailFilter) && (
          <button
            onClick={() => {
              setErrorTypeFilter('');
              setUserEmailFilter('');
            }}
            className="text-sm text-zinc-500 hover:text-zinc-700 underline underline-offset-2"
          >
            Clear filters
          </button>
        )}
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className={cn(
            'ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            isFetching
              ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
              : 'bg-zinc-900 text-white hover:bg-zinc-800',
          )}
        >
          <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
          Refresh
        </button>
        <span className="text-sm text-zinc-400">
          {entries.length} {entries.length === 1 ? 'error' : 'errors'}
        </span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-zinc-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading errors...</span>
          </div>
        </div>
      ) : isError ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
            <p className="text-zinc-600 font-medium">Failed to load errors</p>
            <button
              onClick={() => refetch()}
              className="mt-4 text-sm text-emerald-600 hover:text-emerald-700 underline underline-offset-2"
            >
              Try again
            </button>
          </div>
        </div>
      ) : entries.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">No errors found</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="bg-white rounded-xl border border-zinc-200 p-5 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
                      {entry.error_type}
                    </span>
                    {entry.service && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {entry.service}
                      </span>
                    )}
                    {entry.environment && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {entry.environment}
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-900 font-medium mb-1">{entry.error_message}</p>
                  {entry.request_path && (
                    <p className="text-sm text-zinc-500 font-mono">
                      {entry.request_method} {entry.request_path}
                    </p>
                  )}
                </div>
                <time className="text-xs text-zinc-400 whitespace-nowrap flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDateTime(entry.created_at)}
                </time>
              </div>
              {entry.user_email && (
                <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
                  <User className="w-3.5 h-3.5" />
                  <span>{entry.user_email}</span>
                </div>
              )}
              {entry.stack_trace && (
                <details className="mt-3">
                  <summary className="text-sm text-zinc-600 cursor-pointer hover:text-zinc-900">
                    Stack Trace
                  </summary>
                  <pre className="mt-2 p-3 bg-zinc-50 rounded text-xs overflow-x-auto border border-zinc-200">
                    {entry.stack_trace}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Usage Tab Component
const UsageTab: React.FC<{ password: string }> = ({ password }) => {
  const { data, isLoading, isError, refetch, isFetching } = useUsageStats(password);
  const stats = data?.users ?? [];
  const totals = data?.totals ?? {
    total_tokens: 0,
    prompt_tokens: 0,
    completion_tokens: 0,
    request_count: 0,
  };

  return (
    <div>
      {/* Total Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <div className="text-sm text-zinc-500 mb-2">Total Tokens</div>
          <div className="text-2xl font-bold text-zinc-900">
            {formatNumber(totals.total_tokens)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <div className="text-sm text-zinc-500 mb-2">Prompt Tokens</div>
          <div className="text-2xl font-bold text-zinc-900">
            {formatNumber(totals.prompt_tokens)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <div className="text-sm text-zinc-500 mb-2">Completion Tokens</div>
          <div className="text-2xl font-bold text-zinc-900">
            {formatNumber(totals.completion_tokens)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <div className="text-sm text-zinc-500 mb-2">Total Requests</div>
          <div className="text-2xl font-bold text-zinc-900">
            {formatNumber(totals.request_count)}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-zinc-900">
          User Usage ({data?.total_users ?? 0} users)
        </h2>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            isFetching
              ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
              : 'bg-zinc-900 text-white hover:bg-zinc-800',
          )}
        >
          <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-zinc-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading usage stats...</span>
          </div>
        </div>
      ) : isError ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
            <p className="text-zinc-600 font-medium">Failed to load usage stats</p>
            <button
              onClick={() => refetch()}
              className="mt-4 text-sm text-emerald-600 hover:text-emerald-700 underline underline-offset-2"
            >
              Try again
            </button>
          </div>
        </div>
      ) : stats.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Activity className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">No usage data found</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Total Tokens
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Prompt
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Completion
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Requests
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {stats.map((user, idx) => (
                  <tr key={user.user_id || idx} className="hover:bg-zinc-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-zinc-900">
                        {user.user_email || user.user_id || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-zinc-900">
                      {formatNumber(user.total_tokens)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-zinc-500">
                      {formatNumber(user.prompt_tokens)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-zinc-500">
                      {formatNumber(user.completion_tokens)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-zinc-500">
                      {formatNumber(user.request_count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Users Overview Tab Component
const UsersTab: React.FC<{ password: string }> = ({ password }) => {
  const { data, isLoading, isError, refetch, isFetching } = useUsersOverview(password);
  const users = data?.users ?? [];
  const totals = data?.totals ?? {
    total_tokens: 0,
    prompt_tokens: 0,
    completion_tokens: 0,
    request_count: 0,
    upload_count: 0,
    upload_size: 0,
    conversation_count: 0,
    feedback_count: 0,
  };

  return (
    <div>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Total Users</div>
          <div className="text-xl font-bold text-zinc-900">
            {formatNumber(data?.total_users ?? 0)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Total Tokens</div>
          <div className="text-xl font-bold text-zinc-900">{formatNumber(totals.total_tokens)}</div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Requests</div>
          <div className="text-xl font-bold text-zinc-900">
            {formatNumber(totals.request_count)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Uploads</div>
          <div className="text-xl font-bold text-zinc-900">{formatNumber(totals.upload_count)}</div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Upload Size</div>
          <div className="text-xl font-bold text-zinc-900">{formatBytes(totals.upload_size)}</div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Conversations</div>
          <div className="text-xl font-bold text-zinc-900">
            {formatNumber(totals.conversation_count)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Feedback</div>
          <div className="text-xl font-bold text-zinc-900">
            {formatNumber(totals.feedback_count)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="flex items-center justify-center h-full">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                isFetching
                  ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                  : 'bg-zinc-900 text-white hover:bg-zinc-800',
              )}
            >
              <RefreshCw className={cn('w-3 h-3', isFetching && 'animate-spin')} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-zinc-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading users overview...</span>
          </div>
        </div>
      ) : isError ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
            <p className="text-zinc-600 font-medium">Failed to load users overview</p>
            <button
              onClick={() => refetch()}
              className="mt-4 text-sm text-emerald-600 hover:text-emerald-700 underline underline-offset-2"
            >
              Try again
            </button>
          </div>
        </div>
      ) : users.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <User className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">No users found</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Tokens
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Requests
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Uploads
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Conversations
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Feedback
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Last Active
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {users.map((user, idx) => (
                  <tr key={user.user_id || idx} className="hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-zinc-900">
                          {user.user_email || 'No email'}
                        </div>
                        <div className="text-xs text-zinc-500 font-mono">
                          {user.user_id.slice(0, 8)}...
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-zinc-900">
                        {formatNumber(user.total_tokens)}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {formatNumber(user.prompt_tokens)} / {formatNumber(user.completion_tokens)}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-zinc-900">
                      {formatNumber(user.request_count)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-zinc-900">
                      {formatNumber(user.upload_count)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-zinc-500">
                      {user.upload_size > 0 ? formatBytes(user.upload_size) : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-zinc-500">
                      {user.conversation_count}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-zinc-500">
                      {user.feedback_count}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-zinc-500">
                      {user.last_active ? formatDateTime(user.last_active) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Uploads Tab Component
const UploadsTab: React.FC<{ password: string }> = ({ password }) => {
  const { data, isLoading, isError, refetch, isFetching } = useListUploads(password);
  const clearLegacyMutation = useClearLegacyUploads();
  const deleteUploadMutation = useDeleteUpload();
  const [deleteUploadConfirm, setDeleteUploadConfirm] = useState<{
    source: string;
    displayName: string;
  } | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'by-user'>('all');

  const entries = data?.entries ?? [];
  const legacyCount = data?.legacy_count ?? 0;
  const userScopedCount = data?.user_scoped_count ?? 0;
  const byUser = data?.by_user ?? [];
  const totalSize = data?.total_size ?? 0;

  const handleDeleteUpload = async () => {
    if (!deleteUploadConfirm) return;

    try {
      const result = await deleteUploadMutation.mutateAsync({
        password,
        source: deleteUploadConfirm.source,
      });

      if (result.success) {
        setDeleteUploadConfirm(null);
        setTimeout(() => refetch(), 300);
      } else {
        alert('Delete failed: ' + (result.error || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Delete failed:', error);
      alert('Delete failed: ' + (error?.message || 'Unknown error'));
    }
  };

  const handleClearLegacy = async () => {
    if (
      !confirm(
        `Are you sure you want to delete ${legacyCount} legacy uploads? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      await clearLegacyMutation.mutateAsync({
        password,
        include_unindexed: false,
      });
      // Refresh the list after clearing
      setTimeout(() => refetch(), 500);
    } catch (error) {
      console.error('Failed to clear legacy uploads:', error);
    }
  };

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Total Uploads</div>
          <div className="text-xl font-bold text-zinc-900">{formatNumber(entries.length)}</div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Total Size</div>
          <div className="text-xl font-bold text-zinc-900">{formatBytes(totalSize)}</div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">User-Scoped</div>
          <div className="text-xl font-bold text-emerald-600">{formatNumber(userScopedCount)}</div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Legacy (Unscoped)</div>
          <div className="text-xl font-bold text-rose-600">{formatNumber(legacyCount)}</div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Users with Uploads</div>
          <div className="text-xl font-bold text-blue-600">{formatNumber(byUser.length)}</div>
        </div>
      </div>

      {/* By User Stats - Collapsible */}
      {byUser.length > 0 && (
        <div className="mb-6 bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <button
            onClick={() => setViewMode(viewMode === 'by-user' ? 'all' : 'by-user')}
            className="w-full px-4 py-3 flex items-center justify-between bg-zinc-50 hover:bg-zinc-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-zinc-500" />
              <span className="font-medium text-zinc-900">Uploads by User</span>
              <span className="text-sm text-zinc-500">({byUser.length} users)</span>
            </div>
            <span className="text-sm text-zinc-500">
              {viewMode === 'by-user' ? '▲ Hide' : '▼ Show'}
            </span>
          </button>
          {viewMode === 'by-user' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                      Uploads
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                      Total Size
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200">
                  {byUser.map((userStats, idx) => (
                    <tr key={userStats.user_id || idx} className="hover:bg-zinc-50">
                      <td className="px-4 py-2">
                        <div>
                          <div className="text-sm font-medium text-zinc-900">
                            {userStats.user_email || 'Unknown'}
                          </div>
                          <div className="text-xs text-zinc-500 font-mono">
                            {userStats.user_id.slice(0, 12)}...
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium text-zinc-900">
                        {formatNumber(userStats.upload_count)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-right text-sm text-zinc-500">
                        {formatBytes(userStats.total_size)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between mb-6 p-4 bg-white rounded-xl border border-zinc-200">
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Database className="w-4 h-4" />
          <span className="font-medium">Database Management</span>
        </div>
        <div className="flex items-center gap-3">
          {legacyCount > 0 && (
            <button
              onClick={handleClearLegacy}
              disabled={clearLegacyMutation.isPending || isFetching}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                clearLegacyMutation.isPending || isFetching
                  ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                  : 'bg-rose-600 text-white hover:bg-rose-700',
              )}
            >
              <Trash2 className="w-4 h-4" />
              {clearLegacyMutation.isPending
                ? 'Clearing...'
                : `Clear ${legacyCount} Legacy Uploads`}
            </button>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
              isFetching
                ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                : 'bg-zinc-900 text-white hover:bg-zinc-800',
            )}
          >
            <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {clearLegacyMutation.isSuccess && (
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
          <p className="text-sm text-emerald-800">
            {clearLegacyMutation.data?.message || 'Legacy uploads cleared successfully.'}
          </p>
        </div>
      )}

      {clearLegacyMutation.isError && (
        <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl">
          <p className="text-sm text-rose-800">Failed to clear legacy uploads. Please try again.</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-zinc-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading uploads...</span>
          </div>
        </div>
      ) : isError ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
            <p className="text-zinc-600 font-medium">Failed to load uploads</p>
            <button
              onClick={() => refetch()}
              className="mt-4 text-sm text-emerald-600 hover:text-emerald-700 underline underline-offset-2"
            >
              Try again
            </button>
          </div>
        </div>
      ) : entries.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Upload className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">No uploads found</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    File
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    User ID
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {entries.map((entry, idx) => (
                  <tr key={entry.source || idx} className="hover:bg-zinc-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <File className="w-4 h-4 text-zinc-400" />
                        <div>
                          <div className="text-sm font-medium text-zinc-900">
                            {entry.display_name}
                          </div>
                          <div className="text-xs text-zinc-500 font-mono">{entry.source}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {entry.is_legacy ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
                          Legacy
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          User-Scoped
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-500">
                      {entry.user_id ? (
                        <span className="font-mono">{entry.user_id}</span>
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-zinc-500">
                      {entry.file_size > 0 ? formatBytes(entry.file_size) : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {entry.file_exists ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          On Disk
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-100 text-zinc-500">
                          Index Only
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() =>
                          setDeleteUploadConfirm({
                            source: entry.source,
                            displayName: entry.display_name,
                          })
                        }
                        className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                        title="Delete upload"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Upload Confirmation Modal */}
      {deleteUploadConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">Delete Upload</h3>
            <p className="text-sm text-zinc-600 mb-4">
              Are you sure you want to delete{' '}
              <strong className="text-zinc-900">{deleteUploadConfirm.displayName}</strong>? This
              will remove it from the index. This action cannot be undone.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDeleteUpload}
                disabled={deleteUploadMutation.isPending}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  deleteUploadMutation.isPending
                    ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                    : 'bg-rose-600 text-white hover:bg-rose-700',
                )}
              >
                {deleteUploadMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setDeleteUploadConfirm(null)}
                disabled={deleteUploadMutation.isPending}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-zinc-200 text-zinc-700 hover:bg-zinc-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// Organizations Tab Component
const OrganizationsTab: React.FC<{ password: string }> = ({ password }) => {
  const { data, isLoading, isError, refetch, isFetching } = useOrganizationsStats(password);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-zinc-400 animate-spin" />
        <span className="ml-3 text-zinc-500">Loading organizations...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-rose-900 mb-1">Failed to load organizations</h3>
        <p className="text-sm text-rose-700 mb-4">Please try again later.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const organizations = data?.organizations ?? [];
  const totals = data?.totals ?? {
    total_members: 0,
    total_projects: 0,
    total_tokens_this_month: 0,
    total_tokens_all_time: 0,
    total_uploads: 0,
    total_upload_size: 0,
  };

  return (
    <div>
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Total Organizations</div>
          <div className="text-xl font-bold text-zinc-900">
            {formatNumber(data?.total_organizations ?? 0)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Total Members</div>
          <div className="text-xl font-bold text-blue-600">
            {formatNumber(totals.total_members)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Total Projects</div>
          <div className="text-xl font-bold text-emerald-600">
            {formatNumber(totals.total_projects)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Tokens (This Month)</div>
          <div className="text-xl font-bold text-purple-600">
            {formatNumber(totals.total_tokens_this_month)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Total Uploads</div>
          <div className="text-xl font-bold text-amber-600">
            {formatNumber(totals.total_uploads)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="text-xs text-zinc-500 mb-1">Upload Size</div>
          <div className="text-xl font-bold text-indigo-600">
            {formatBytes(totals.total_upload_size)}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between mb-6 p-4 bg-white rounded-xl border border-zinc-200">
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Users className="w-4 h-4" />
          <span className="font-medium">Organization Statistics</span>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
            isFetching
              ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
              : 'bg-zinc-900 text-white hover:bg-zinc-800',
          )}
        >
          <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Organizations Table */}
      {organizations.length === 0 ? (
        <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center">
          <Users className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-zinc-900 mb-1">No organizations found</h3>
          <p className="text-sm text-zinc-500">
            Organizations will appear here once they are created.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    Members
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    Projects
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    Tokens (Month)
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    Tokens (All Time)
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    Uploads
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    Upload Size
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {organizations.map((org) => (
                  <tr key={org.org_id} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-zinc-900">
                          {org.name || org.org_id}
                        </span>
                        <span className="text-xs text-zinc-500 font-mono">{org.org_id}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-1 rounded text-xs font-medium',
                          org.plan === 'enterprise'
                            ? 'bg-purple-100 text-purple-700'
                            : org.plan === 'elite'
                              ? 'bg-blue-100 text-blue-700'
                              : org.plan === 'pro'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-zinc-100 text-zinc-700',
                        )}
                      >
                        {org.plan || 'free'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-zinc-900 font-medium">
                      {formatNumber(org.member_count)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-zinc-900 font-medium">
                      {formatNumber(org.project_count)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-zinc-500">
                      {formatNumber(org.tokens_this_month)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-zinc-900 font-medium">
                      {formatNumber(org.total_tokens)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-zinc-500">
                      {formatNumber(org.upload_count)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-zinc-500">
                      {formatBytes(org.upload_size)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-zinc-500">
                      {org.created_at ? formatDateTime(org.created_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Database Tab Component
const DatabaseTab: React.FC<{ password: string }> = ({ password }) => {
  const [selectedDatabase, setSelectedDatabase] = useState<string | undefined>(undefined);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<'schema' | 'data' | 'query'>('schema');
  const [query, setQuery] = useState('SELECT * FROM users LIMIT 10');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [editingRow, setEditingRow] = useState<{
    index: number;
    values: Record<string, any>;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ index: number; row: any[] } | null>(null);

  const { data: databasesList } = useListDatabases(password);
  const selectDbMutation = useSelectDatabase();
  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
    refetch: refetchStats,
  } = useDatabaseStats(password, selectedDatabase);
  const {
    data: schema,
    isLoading: schemaLoading,
    refetch: refetchSchema,
  } = useTableSchema(password, selectedTable || '', selectedDatabase, !!selectedTable);
  const {
    data: tableData,
    isLoading: dataLoading,
    refetch: refetchTableData,
  } = useTableData(
    password,
    selectedTable || '',
    page,
    pageSize,
    selectedDatabase,
    !!selectedTable,
  );
  const queryMutation = useExecuteQuery();
  const exportMutation = useExportDatabase();
  const updateMutation = useUpdateRows();
  const deleteMutation = useDeleteRows();
  const clearTableMutation = useClearTable();
  const [clearTableConfirm, setClearTableConfirm] = useState<string | null>(null);

  const handleSelectDatabase = async (dbPath: string) => {
    try {
      await selectDbMutation.mutateAsync({ password, databasePath: dbPath });
      setSelectedDatabase(dbPath);
      setSelectedTable(null);
      setPage(1);
      refetchStats();
    } catch (error) {
      console.error('Failed to select database:', error);
    }
  };

  const handleUpdateRow = async (rowIndex: number) => {
    if (!selectedTable || !tableData || !editingRow) return;

    const row = tableData.rows[rowIndex];
    const where: Record<string, any> = {};
    const setValues: Record<string, any> = {};

    // Use primary key columns for WHERE clause
    const pkColumns = schema?.columns.filter((col) => col.pk).map((col) => col.name) || [];
    if (pkColumns.length === 0) {
      alert('Cannot update: Table has no primary key');
      return;
    }

    pkColumns.forEach((col) => {
      const colIndex = tableData.columns.indexOf(col);
      if (colIndex >= 0) {
        where[col] = row[colIndex];
      }
    });

    // Set updated values
    tableData.columns.forEach((col, idx) => {
      if (editingRow.values[col] !== undefined && editingRow.values[col] !== row[idx]) {
        setValues[col] = editingRow.values[col];
      }
    });

    if (Object.keys(setValues).length === 0) {
      alert('No changes to save');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        password,
        request: { table: selectedTable, set_values: setValues, where },
        databasePath: selectedDatabase,
      });
      setEditingRow(null);
      refetchTableData();
      refetchSchema();
    } catch (error) {
      console.error('Update failed:', error);
      alert('Update failed: ' + (error as Error).message);
    }
  };

  const handleDeleteRow = async () => {
    if (!selectedTable || !tableData || !deleteConfirm) return;

    const row = tableData.rows[deleteConfirm.index];
    const where: Record<string, any> = {};

    // Use primary key columns for WHERE clause, fallback to all columns if no PK
    const pkColumns = schema?.columns.filter((col) => col.pk).map((col) => col.name) || [];

    if (pkColumns.length > 0) {
      // Use primary key
      pkColumns.forEach((col) => {
        const colIndex = tableData.columns.indexOf(col);
        if (colIndex >= 0) {
          where[col] = row[colIndex];
        }
      });
    } else {
      // Fallback: use all columns for WHERE clause
      tableData.columns.forEach((col, idx) => {
        where[col] = row[idx];
      });
    }

    if (Object.keys(where).length === 0) {
      alert('Cannot delete: Unable to identify row');
      return;
    }

    try {
      const result = await deleteMutation.mutateAsync({
        password,
        request: { table: selectedTable, where },
        databasePath: selectedDatabase,
      });

      if (result.success) {
        setDeleteConfirm(null);
        refetchTableData();
        refetchSchema();
        if (result.rows_affected === 0) {
          alert(
            'No rows were deleted. The row may have already been deleted or the WHERE conditions did not match.',
          );
        }
      } else {
        alert('Delete failed: ' + (result.error || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Delete failed:', error);
      const errorMessage =
        error?.response?.json?.()?.detail?.message || error?.message || 'Unknown error';
      alert('Delete failed: ' + errorMessage);
    }
  };

  const handleExport = async (format: 'json' | 'csv' = 'json') => {
    try {
      const result = await exportMutation.mutateAsync({ password, format });
      // Create download link
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `database-export-${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleClearTable = async () => {
    if (!clearTableConfirm) return;

    try {
      const result = await clearTableMutation.mutateAsync({
        password,
        tableName: clearTableConfirm,
        databasePath: selectedDatabase,
      });

      if (result.success) {
        setClearTableConfirm(null);
        refetchStats();
        refetchTableData();
        refetchSchema();
      } else {
        alert('Clear table failed: ' + (result.error || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Clear table failed:', error);
      alert('Clear table failed: ' + (error?.message || 'Unknown error'));
    }
  };

  return (
    <div>
      {/* Database Selection */}
      {databasesList && (
        <div className="mb-6 p-4 bg-white rounded-xl border border-zinc-200">
          <label className="block text-sm font-medium text-zinc-700 mb-2">Select Database</label>
          <select
            value={selectedDatabase || ''}
            onChange={(e) => {
              const dbPath = e.target.value || undefined;
              if (dbPath) {
                handleSelectDatabase(dbPath);
              } else {
                setSelectedDatabase(undefined);
                setSelectedTable(null);
              }
            }}
            className="w-full px-4 py-2 rounded-lg border border-zinc-200 text-sm bg-white hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
          >
            <option value="">Default Database (from settings)</option>
            {databasesList.databases.map((db) => (
              <option key={db.path} value={db.path}>
                {db.name} ({formatBytes(db.size_bytes)}) {db.is_current ? '(Current)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Database Stats */}
      {statsLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-zinc-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading database stats...</span>
          </div>
        </div>
      ) : statsError ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
            <p className="text-zinc-600 font-medium">Failed to load database stats</p>
            <button
              onClick={() => refetchStats()}
              className="mt-4 text-sm text-emerald-600 hover:text-emerald-700 underline underline-offset-2"
            >
              Try again
            </button>
          </div>
        </div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-zinc-200 p-5">
              <div className="text-sm text-zinc-500 mb-2">Database Path</div>
              <div className="text-sm font-mono text-zinc-900 truncate" title={stats.database_path}>
                {stats.database_path}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-5">
              <div className="text-sm text-zinc-500 mb-2">Database Size</div>
              <div className="text-2xl font-bold text-zinc-900">
                {formatBytes(stats.database_size_bytes)}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-5">
              <div className="text-sm text-zinc-500 mb-2">Total Tables</div>
              <div className="text-2xl font-bold text-zinc-900">{stats.tables.length}</div>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-5">
              <div className="text-sm text-zinc-500 mb-2">Total Rows</div>
              <div className="text-2xl font-bold text-zinc-900">
                {formatNumber(stats.total_rows)}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mb-6 p-4 bg-white rounded-xl border border-zinc-200">
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Database className="w-4 h-4" />
              <span className="font-medium">Database Management</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleExport('json')}
                disabled={exportMutation.isPending}
                className={cn(
                  'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                  exportMutation.isPending
                    ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                    : 'bg-zinc-900 text-white hover:bg-zinc-800',
                )}
              >
                <Download className="w-4 h-4" />
                {exportMutation.isPending ? 'Exporting...' : 'Export JSON'}
              </button>
              <button
                onClick={() => refetchStats()}
                disabled={statsLoading}
                className={cn(
                  'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                  statsLoading
                    ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                    : 'bg-zinc-900 text-white hover:bg-zinc-800',
                )}
              >
                <RefreshCw className={cn('w-4 h-4', statsLoading && 'animate-spin')} />
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tables List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-zinc-200 p-4">
                <h3 className="text-lg font-semibold text-zinc-900 mb-4">Tables</h3>
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {stats.tables.map((table) => (
                    <button
                      key={table.name}
                      onClick={() => {
                        setSelectedTable(table.name);
                        setSelectedView('schema');
                        setPage(1);
                      }}
                      className={cn(
                        'w-full text-left p-3 rounded-lg border transition-all',
                        selectedTable === table.name
                          ? 'bg-zinc-900 text-white border-zinc-900'
                          : 'bg-zinc-50 border-zinc-200 hover:border-zinc-300 hover:bg-zinc-100',
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Table className="w-4 h-4" />
                          <span className="font-medium">{table.name}</span>
                        </div>
                        <span
                          className={cn(
                            'text-xs px-2 py-1 rounded',
                            selectedTable === table.name
                              ? 'bg-white/20 text-white'
                              : 'bg-zinc-200 text-zinc-600',
                          )}
                        >
                          {formatNumber(stats.table_row_counts[table.name] || 0)}
                        </span>
                      </div>
                      <div
                        className={cn(
                          'text-xs mt-1',
                          selectedTable === table.name ? 'text-zinc-300' : 'text-zinc-500',
                        )}
                      >
                        {table.type}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Table Details */}
            <div className="lg:col-span-2">
              {selectedTable ? (
                <div className="bg-white rounded-xl border border-zinc-200">
                  {/* Tabs */}
                  <div className="flex gap-2 border-b border-zinc-200 p-4">
                    <button
                      onClick={() => setSelectedView('schema')}
                      className={cn(
                        'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                        selectedView === 'schema'
                          ? 'border-emerald-500 text-emerald-600'
                          : 'border-transparent text-zinc-500 hover:text-zinc-700',
                      )}
                    >
                      Schema
                    </button>
                    <button
                      onClick={() => setSelectedView('data')}
                      className={cn(
                        'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                        selectedView === 'data'
                          ? 'border-emerald-500 text-emerald-600'
                          : 'border-transparent text-zinc-500 hover:text-zinc-700',
                      )}
                    >
                      Data
                    </button>
                    <button
                      onClick={() => setSelectedView('query')}
                      className={cn(
                        'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                        selectedView === 'query'
                          ? 'border-emerald-500 text-emerald-600'
                          : 'border-transparent text-zinc-500 hover:text-zinc-700',
                      )}
                    >
                      Query
                    </button>
                  </div>

                  {/* Schema View */}
                  {selectedView === 'schema' && (
                    <div className="p-6">
                      {schemaLoading ? (
                        <div className="flex items-center justify-center py-10">
                          <RefreshCw className="w-5 h-5 animate-spin text-zinc-400" />
                        </div>
                      ) : schema ? (
                        <div>
                          <h4 className="text-lg font-semibold text-zinc-900 mb-4">
                            {schema.table_name} ({formatNumber(schema.row_count)} rows)
                          </h4>

                          {/* Columns */}
                          <div className="mb-6">
                            <h5 className="text-sm font-medium text-zinc-700 mb-3">Columns</h5>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-zinc-50">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500">
                                      Name
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500">
                                      Type
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500">
                                      Nullable
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500">
                                      Primary Key
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500">
                                      Default
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-200">
                                  {schema.columns.map((col, idx) => (
                                    <tr key={idx}>
                                      <td className="px-4 py-2 font-mono text-zinc-900">
                                        {col.name}
                                      </td>
                                      <td className="px-4 py-2 text-zinc-600">{col.type}</td>
                                      <td className="px-4 py-2 text-zinc-600">
                                        {col.notnull ? 'No' : 'Yes'}
                                      </td>
                                      <td className="px-4 py-2 text-zinc-600">
                                        {col.pk ? '✓' : ''}
                                      </td>
                                      <td className="px-4 py-2 text-zinc-500 font-mono text-xs">
                                        {col.dflt_value || '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Indexes */}
                          {schema.indexes.length > 0 && (
                            <div>
                              <h5 className="text-sm font-medium text-zinc-700 mb-3">Indexes</h5>
                              <div className="space-y-2">
                                {schema.indexes.map((idx, idxIdx) => (
                                  <div key={idxIdx} className="p-3 bg-zinc-50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-sm text-zinc-900">
                                        {idx.name}
                                      </span>
                                      {idx.unique && (
                                        <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                                          UNIQUE
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-zinc-600 mt-1">
                                      Columns: {idx.columns.join(', ')}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}

                  {/* Data View */}
                  {selectedView === 'data' && (
                    <div className="p-6">
                      {dataLoading ? (
                        <div className="flex items-center justify-center py-10">
                          <RefreshCw className="w-5 h-5 animate-spin text-zinc-400" />
                        </div>
                      ) : tableData ? (
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <h4 className="text-lg font-semibold text-zinc-900">
                                {selectedTable} ({formatNumber(tableData.total_rows)} total rows)
                              </h4>
                              {tableData.total_rows > 0 && (
                                <button
                                  onClick={() => setClearTableConfirm(selectedTable)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Clear Table
                                </button>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <select
                                value={pageSize}
                                onChange={(e) => {
                                  setPageSize(Number(e.target.value));
                                  setPage(1);
                                }}
                                className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white"
                              >
                                <option value={25}>25 per page</option>
                                <option value={50}>50 per page</option>
                                <option value={100}>100 per page</option>
                                <option value={250}>250 per page</option>
                              </select>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                                  disabled={page === 1}
                                  className="px-3 py-2 rounded-lg border border-zinc-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-zinc-50"
                                >
                                  Previous
                                </button>
                                <span className="text-sm text-zinc-600">
                                  Page {page} of {Math.ceil(tableData.total_rows / pageSize)}
                                </span>
                                <button
                                  onClick={() => setPage((p) => p + 1)}
                                  disabled={page >= Math.ceil(tableData.total_rows / pageSize)}
                                  className="px-3 py-2 rounded-lg border border-zinc-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-zinc-50"
                                >
                                  Next
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="overflow-x-auto border border-zinc-200 rounded-lg">
                            <table className="w-full text-sm">
                              <thead className="bg-zinc-50">
                                <tr>
                                  {tableData.columns.map((col) => (
                                    <th
                                      key={col}
                                      className="px-4 py-2 text-left text-xs font-medium text-zinc-500"
                                    >
                                      {col}
                                    </th>
                                  ))}
                                  <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-zinc-200">
                                {tableData.rows.map((row, rowIdx) => (
                                  <tr key={rowIdx} className="hover:bg-zinc-50">
                                    {row.map((cell, cellIdx) => (
                                      <td key={cellIdx} className="px-4 py-2 text-zinc-900">
                                        {editingRow?.index === rowIdx ? (
                                          <input
                                            type="text"
                                            value={
                                              editingRow.values[tableData.columns[cellIdx]] ??
                                              cell ??
                                              ''
                                            }
                                            onChange={(e) => {
                                              setEditingRow({
                                                ...editingRow,
                                                values: {
                                                  ...editingRow.values,
                                                  [tableData.columns[cellIdx]]: e.target.value,
                                                },
                                              });
                                            }}
                                            className="w-full px-2 py-1 rounded border border-zinc-300 font-mono text-xs"
                                          />
                                        ) : cell === null ? (
                                          <span className="text-zinc-400 italic">NULL</span>
                                        ) : typeof cell === 'string' && cell.length > 100 ? (
                                          <span className="font-mono text-xs" title={cell}>
                                            {cell.substring(0, 100)}...
                                          </span>
                                        ) : (
                                          <span className="font-mono text-xs">{String(cell)}</span>
                                        )}
                                      </td>
                                    ))}
                                    <td className="px-4 py-2">
                                      <div className="flex items-center gap-2">
                                        {editingRow?.index === rowIdx ? (
                                          <>
                                            <button
                                              onClick={() => handleUpdateRow(rowIdx)}
                                              disabled={updateMutation.isPending}
                                              className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
                                              title="Save"
                                            >
                                              <Check className="w-4 h-4" />
                                            </button>
                                            <button
                                              onClick={() => setEditingRow(null)}
                                              className="p-1.5 text-zinc-600 hover:bg-zinc-100 rounded"
                                              title="Cancel"
                                            >
                                              <X className="w-4 h-4" />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button
                                              onClick={() =>
                                                setEditingRow({ index: rowIdx, values: {} })
                                              }
                                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                              title="Edit"
                                            >
                                              <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                              onClick={() =>
                                                setDeleteConfirm({ index: rowIdx, row })
                                              }
                                              className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"
                                              title="Delete"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : null}

                      {/* Delete Row Confirmation Modal */}
                      {deleteConfirm && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
                            <h3 className="text-lg font-semibold text-zinc-900 mb-4">
                              Confirm Delete
                            </h3>
                            <p className="text-sm text-zinc-600 mb-4">
                              Are you sure you want to delete this row? This action cannot be
                              undone.
                            </p>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={handleDeleteRow}
                                disabled={deleteMutation.isPending}
                                className={cn(
                                  'px-4 py-2 rounded-lg text-sm font-medium',
                                  deleteMutation.isPending
                                    ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                                    : 'bg-rose-600 text-white hover:bg-rose-700',
                                )}
                              >
                                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-4 py-2 rounded-lg text-sm font-medium bg-zinc-200 text-zinc-700 hover:bg-zinc-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Clear Table Confirmation Modal */}
                      {clearTableConfirm && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="p-2 bg-rose-100 rounded-lg">
                                <AlertCircle className="w-6 h-6 text-rose-600" />
                              </div>
                              <h3 className="text-lg font-semibold text-zinc-900">Clear Table</h3>
                            </div>
                            <p className="text-sm text-zinc-600 mb-2">
                              Are you sure you want to delete{' '}
                              <strong className="text-zinc-900">ALL rows</strong> from{' '}
                              <strong className="text-zinc-900">{clearTableConfirm}</strong>?
                            </p>
                            <p className="text-sm text-rose-600 mb-4 p-3 bg-rose-50 rounded-lg border border-rose-200">
                              This will permanently delete{' '}
                              {formatNumber(tableData?.total_rows || 0)} row(s). This action cannot
                              be undone.
                            </p>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={handleClearTable}
                                disabled={clearTableMutation.isPending}
                                className={cn(
                                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                                  clearTableMutation.isPending
                                    ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                                    : 'bg-rose-600 text-white hover:bg-rose-700',
                                )}
                              >
                                {clearTableMutation.isPending ? 'Clearing...' : 'Clear All Rows'}
                              </button>
                              <button
                                onClick={() => setClearTableConfirm(null)}
                                disabled={clearTableMutation.isPending}
                                className="px-4 py-2 rounded-lg text-sm font-medium bg-zinc-200 text-zinc-700 hover:bg-zinc-300 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Query View */}
                  {selectedView === 'query' && (
                    <div className="p-6">
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-zinc-700 mb-2">
                          SQL Query (SELECT only)
                        </label>
                        <textarea
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          className="w-full h-32 px-4 py-3 rounded-lg border border-zinc-200 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
                          placeholder="SELECT * FROM users LIMIT 10"
                        />
                      </div>
                      <button
                        onClick={() => {
                          queryMutation.mutate({
                            password,
                            request: { query, limit: 100 },
                            databasePath: selectedDatabase,
                          });
                        }}
                        disabled={queryMutation.isPending || !query.trim()}
                        className={cn(
                          'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                          queryMutation.isPending || !query.trim()
                            ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                            : 'bg-emerald-600 text-white hover:bg-emerald-700',
                        )}
                      >
                        {queryMutation.isPending ? 'Executing...' : 'Execute Query'}
                      </button>

                      {queryMutation.data && (
                        <div className="mt-6">
                          {queryMutation.data.error ? (
                            <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg">
                              <p className="text-sm text-rose-800 font-mono">
                                {queryMutation.data.error}
                              </p>
                            </div>
                          ) : (
                            <div>
                              <div className="flex items-center justify-between mb-4">
                                <div className="text-sm text-zinc-600">
                                  {formatNumber(queryMutation.data.row_count)} rows in{' '}
                                  {queryMutation.data.execution_time_ms.toFixed(2)}ms
                                </div>
                              </div>
                              <div className="overflow-x-auto border border-zinc-200 rounded-lg">
                                <table className="w-full text-sm">
                                  <thead className="bg-zinc-50">
                                    <tr>
                                      {queryMutation.data.columns.map((col) => (
                                        <th
                                          key={col}
                                          className="px-4 py-2 text-left text-xs font-medium text-zinc-500"
                                        >
                                          {col}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-zinc-200">
                                    {queryMutation.data.rows.map((row, rowIdx) => (
                                      <tr key={rowIdx} className="hover:bg-zinc-50">
                                        {row.map((cell, cellIdx) => (
                                          <td key={cellIdx} className="px-4 py-2 text-zinc-900">
                                            {cell === null ? (
                                              <span className="text-zinc-400 italic">NULL</span>
                                            ) : typeof cell === 'string' && cell.length > 100 ? (
                                              <span
                                                className="font-mono text-xs"
                                                title={String(cell)}
                                              >
                                                {String(cell).substring(0, 100)}...
                                              </span>
                                            ) : (
                                              <span className="font-mono text-xs">
                                                {String(cell)}
                                              </span>
                                            )}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center">
                  <Table className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                  <p className="text-zinc-500">Select a table to view details</p>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export const DevPage: React.FC = () => {
  const [authenticatedPassword, setAuthenticatedPassword] = useState<string | null>(null);

  if (!authenticatedPassword) {
    return <PasswordGate onAuthenticated={setAuthenticatedPassword} />;
  }

  return <DevDashboard password={authenticatedPassword} />;
};

export default DevPage;
